import type { StatusRole } from '@lighthouse/design'
import { NOT_MEASURED, exactNumber, shortDuration } from '../../utils/format'
import type {
  SourceFaultKind, SourceHealth, SourceObservationKind, SourceReachability,
} from '../../types/observatory'

/**
 * Presentation for source connection health.
 *
 * Every helper here is a wording decision, not a calculation. `GET
 * api/observatory/sources` already derives reachability (a strict-precedence
 * ladder in which a stale projection outranks every other signal), the status
 * role, its label, the operator-facing detail sentence, `isUsable`, the three
 * withholding gates and the three header counts. Each of those has a recorded
 * bug behind it and a client-side "correction" reintroduces it. **Nothing in
 * this file recomputes any of them.**
 *
 * Two deliberate exceptions, named so the fence stays legible:
 *
 *  - {@link groupByConnector} is a layout decision rather than a wording one. It
 *    reshapes the served list without reordering it, and the reason it must not
 *    reorder is stated on the function.
 *  - {@link inventoryLabel} counts distinct connectors and hosts. That is the
 *    SHAPE of the list, not a re-sum of the usable/faulted/unobserved partition,
 *    which the page must never recompute or extend with a fourth count.
 *
 * `whenLabel` and `agoLabel` are not here at all. They are generic, this page was
 * the second to want them, and they have been moved to `utils/format.ts` — which
 * is where callers should import them from. A copy here, or a re-export through
 * the workers folder, would both be the thing the `NOT_MEASURED` rule exists to
 * stop: one behaviour with more than one home.
 */

/**
 * "1 source on 1 host", "2 sources on 3 hosts, 5 readings".
 *
 * A reader who does not know the fleet size cannot tell one row from a truncated
 * list, and one greyed-out row with no context reads as a broken page rather
 * than as the honest normal state. So the page says the number out loud.
 *
 * The reading count is appended only when it differs from the connector count —
 * that is, only when the composite key is actually doing something. It is the
 * length of the list that follows, never a total assembled from the header
 * counts, so it cannot disagree with the rows underneath it.
 */
export function inventoryLabel(sources: SourceHealth[]): string {
  const connectors = new Set(sources.map((s) => s.connectorId)).size
  const hosts = new Set(sources.map((s) => s.hostName)).size
  const head = `${plural(connectors, 'source')} on ${plural(hosts, 'host')}`
  return connectors === sources.length ? head : `${head}, ${plural(sources.length, 'reading')}`
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

/**
 * Rows for one connector, in the order the server sent them.
 *
 * The server orders by connector then host **so that rows which disagree about
 * one source sit next to each other**, and that adjacency is the entire reason
 * the projection is keyed on the pair: "three of four hosts can reach Oracle" is
 * a firewall problem, and it is only visible when the four rows are read
 * together. Sorting or de-duplicating here would destroy the one diagnosis the
 * composite key exists to enable.
 *
 * So this walks adjacency rather than bucketing by id. If the server ever
 * stopped ordering, the same connector would appear as two groups — visibly
 * odd, and far safer than silently reordering rows the server arranged on
 * purpose.
 */
export function groupByConnector(sources: SourceHealth[]): { connectorId: string; rows: SourceHealth[] }[] {
  const groups: { connectorId: string; rows: SourceHealth[] }[] = []
  for (const source of sources) {
    const current = groups[groups.length - 1]
    if (current && current.connectorId === source.connectorId) current.rows.push(source)
    else groups.push({ connectorId: source.connectorId, rows: [source] })
  }
  return groups
}

/**
 * What the reading actually rests on.
 *
 * A probe and real traffic are not equal evidence and the server keeps them
 * apart, so the page says which it has rather than flattening both into
 * "checked". A probe proves a listener answers `SELECT 1 FROM DUAL`; traffic
 * proves the source served the query the ETL actually runs, under the
 * credentials it actually uses — which is the stronger claim by some distance,
 * since a listener can answer while the account is locked out of every table.
 */
export function observedViaLabel(kind: SourceObservationKind): string {
  switch (kind) {
    case 'Traffic': return 'Traffic — the source served a query the ETL actually runs'
    case 'Probe': return 'Probe — a listener answered a trivial query'
    case 'Never': return 'Nothing has observed it'
    default: return kind
  }
}

/**
 * The last observed outcome, spelled out.
 *
 * Present tense despite the field name: a success resets it to `None`, and so
 * does a worker restart. `None` beside a dated `lastFailureDetail` is therefore
 * honest rather than contradictory, and the page says so beneath the pair.
 *
 * `CircuitOpen` gets the longest gloss because it is the one value that says
 * what THIS HOST did rather than what the source is doing — the source was not
 * contacted at all.
 */
export function faultKindLabel(kind: SourceFaultKind): string {
  switch (kind) {
    case 'None': return 'None recorded since the last success or restart'
    case 'Transient': return 'Transient — a retry may well clear it'
    case 'Unreachable': return 'Unreachable — nothing answered'
    case 'Rejected': return 'Rejected — the source answered and refused us'
    case 'MissingObject': return 'Missing object — the source answered, the object was not there'
    case 'CircuitOpen': return 'Circuit open — this host declined to dial; the source was never contacted'
    default: return kind
  }
}

/**
 * How long the last SUCCESSFUL call took, or a dash until one succeeds.
 *
 * Loose `== null` here and in the three withheld-field helpers below, deliberately.
 * It catches `undefined` as well, and `undefined` is one serializer setting away:
 * the API serializes nulls explicitly today, but that is held up by a comment in
 * another project (`Lighthouse.Api/Program.cs` — "do not reintroduce
 * DefaultIgnoreCondition = WhenWritingNull"), not by a type. Under a strict
 * `=== null` a reinstated `WhenWritingNull` would not blank these fields, it would
 * invert them: a withheld permanent-fault flag would start reading "No — the last
 * fault is one a retry may clear". Every dash on this page would turn into a
 * confident answer, which is the exact failure the page was built to delete.
 */
export function latencyLabel(ms: number | null): string {
  if (ms == null) return NOT_MEASURED
  if (ms < 1000) return `${ms} ms`
  return shortDuration(ms / 1000)
}

/**
 * The failure streak — **withheld and zero must not look alike**.
 *
 * `null` is the server declining to answer because nothing is publishing: an
 * open-ended running total whose clock has stopped is a partial measurement
 * presented as a total, and "7 consecutive failures" beside a dash reads as
 * seven and climbing. A measured zero is a real reading and is printed plainly,
 * with no status role and no reassurance attached to it.
 */
export function failureCountLabel(count: number | null): string {
  if (count == null) return NOT_MEASURED
  return exactNumber(count)
}

/**
 * Whether a retry can clear the last fault — or the server declining to advise.
 *
 * The dash here is the most important one on the page after `status` itself.
 * `false` sends a reader off to retry and `true` sends them to phone somebody;
 * both are confident instructions, and the server withholds them whenever its
 * own verdict is `Unknown` precisely so that neither is manufactured out of
 * ignorance. Rendering `null` as "no permanent fault" would put back the
 * cleared-fault fiction the withholding exists to prevent.
 */
export function permanentFaultLabel(isPermanentFault: boolean | null): string {
  if (isPermanentFault == null) return NOT_MEASURED
  return isPermanentFault
    ? 'Yes — a retry cannot clear this; it needs a person'
    : 'No — the last fault is one a retry may clear'
}

/**
 * Whether anything on this host has ever dialled the source.
 *
 * Distinct from having failed, and withheld while the projection is stale
 * because the connector may well have gone on attempting contact after the
 * publisher stopped saying so. `lastAttemptAt` stays true as of `observedAt`
 * and is shown regardless, so the dash costs the reader nothing.
 */
export function contactHistoryLabel(hasNeverBeenContacted: boolean | null): string {
  if (hasNeverBeenContacted == null) return NOT_MEASURED
  return hasNeverBeenContacted
    ? 'Never — nothing on this host has dialled it'
    : 'Yes, at least once'
}

/**
 * The usability gate, worded as the gate it is.
 *
 * "May I rely on this source?" — so an unobserved source is No, and that is the
 * governing rule being obeyed rather than a verdict that the source is bad. The
 * header keeps unobserved and faulted in separate counts for exactly that
 * reason, and this wording avoids implying either.
 */
export function usableLabel(isUsable: boolean): string {
  return isUsable
    ? 'Yes — the connector may rely on it'
    : 'No — the connector must not rely on it'
}

/**
 * The instruction behind a dash, which differs by which dash it is.
 *
 * `Unknown` and `NotConfigured` share a glyph and have opposite next steps, and
 * the server's ladder keeps them apart specifically so this page can. Returns
 * null for every state that has a status role, since those carry their own
 * meaning in the badge.
 *
 * **`Unknown` needs a second input, and getting this wrong sends an operator to
 * the wrong system.** The server derives `Unknown` from three different causes —
 * the projection is stale, the source has been idle past the freshness window, or
 * nothing has ever contacted it — and collapses all three into one enum value,
 * putting the cause in `detail` alone. On the stale cause the obvious advice is
 * actively wrong: running a load when the publisher has stopped produces
 * observations nobody records, and it sends someone to Oracle when the fault is
 * in Automaton. So `isProjectionStale` is taken as an argument rather than
 * inferred. It is a served field, not a re-derivation — the page is reading the
 * server's own flag to choose wording, which is the whole job of this module.
 *
 * The remaining two causes share an instruction because they share a next step:
 * in both, nothing has looked recently and making something look is what
 * resolves it.
 */
export function dashInstruction(
  reachability: SourceReachability,
  isProjectionStale: boolean,
): string | null {
  switch (reachability) {
    case 'Unknown':
      return isProjectionStale
        ? 'Check the observer, not the source. Nothing is publishing these readings, so no load you run '
          + 'will restore this one — and the source itself may be perfectly healthy.'
        : 'Wait, or run a load. This is not a claim that the source is down — it is the absence of a claim.'
    case 'NotConfigured':
      return 'Stop asking. This host holds no usable connector for the source, so nothing here will ever contact it.'
    default:
      return null
  }
}

/**
 * Which of the three withheld fields are actually withheld on this row.
 *
 * The page renders its explanatory footnote only when there is something to
 * explain. On the same row one derived flag can be withheld while two
 * measurements are present — the honest normal state does exactly that — so
 * "all dashes" and "no dashes" are both wrong answers.
 */
export function withheldFields(source: SourceHealth): string[] {
  const withheld: string[] = []
  if (source.isPermanentFault == null) withheld.push('whether the fault is permanent')
  if (source.hasNeverBeenContacted == null) withheld.push('whether contact has ever been attempted')
  if (source.consecutiveFailures == null) withheld.push('the consecutive-failure count')
  return withheld
}

/**
 * The Mantine alert tone for a detail sentence.
 *
 * `null` maps to a NEUTRAL grey, never to green and never to a fault colour. An
 * unobserved source is not bad and it is not fine; giving the non-answer its own
 * alarming colour would turn it into an answer just as surely as a green tick
 * would. `serious` takes orange and `critical` red so that "it refused us" and
 * "it is not answering" stay visibly different — they send an operator to
 * different people.
 */
export function detailTone(status: StatusRole | null): string {
  switch (status) {
    case 'good': return 'teal'
    case 'warning': return 'yellow'
    case 'serious': return 'orange'
    case 'critical': return 'red'
    default: return 'gray'
  }
}
